import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AdvisorDashboardTopBar, updatePriorityStatuses } from '..';
import {
  selectAdvisorHasClients,
  selectPriorityStatusByEngagement,
  selectSessionAdvisor,
  selectShouldDisallowUsage,
} from '../model/selectors';

import { AdvisorAccountActionRequired } from '@client/pages/AdvisorAccountActionRequired';
import { AdvisorAccountSponsorship } from '@client/pages/AdvisorAccountSponsorship';
import { AdvisorWelcome } from '@client/pages/AdvisorWelcome';
import { Bars3Icon } from '@heroicons/react/20/solid';
import { Menu } from 'lucide-react';
import { AdvisorDashboardClients } from '../pages/AdvisorDashboardClients';
import {
  AdvisorDashboardOpportunities,
  useFetchQualifyingClients,
} from '../pages/AdvisorDashboardOpportunities';
import { AdvisorDashboardSettings } from '../pages/AdvisorDashboardSettings';
import { useAdvisorNavigation } from './AdvisorDashboardRouteGuard';
import {
  AdvisorDashboardSideBar,
  AdvisorDashboardSideBarContent,
  AdvisorDashboardSideBarMobile,
} from './AdvisorDashboardSideBar';
import { InviteClientModal } from './InviteClientModal';
import { useSignOut } from './hooks/use-sign-out';
import { Button } from './ui/button';

export type DashboardActivePage = 'clients' | 'settings' | 'opportunities' | 'sponsorship';

// Custom hooks for AdvisorDashboardShell
const useActivePage = () => {
  const location = useLocation();

  const getActivePageFromPath = (): DashboardActivePage => {
    const path = location.pathname;
    const normalizedPath = path.replace(/\/advisors\/[^/]+/, '/advisors');

    if (normalizedPath.includes('/settings')) return 'settings';
    else if (normalizedPath.includes('/opportunities')) return 'opportunities';
    else if (normalizedPath.includes('/sponsorship')) return 'sponsorship';
    else return 'clients';
  };

  const [activePage, setActivePage] = useState<DashboardActivePage>(getActivePageFromPath());

  useEffect(() => {
    const newActivePage = getActivePageFromPath();
    setActivePage(newActivePage);
  }, [location.pathname]);

  return { activePage, getActivePageFromPath };
};

const useModals = () => {
  const [showInviteClientModal, setShowInviteClientModal] = useState(false);
  const [shouldPrepopulateEmail, setShouldPrepopulateEmail] = useState(false);
  const [showIntakeFormLinkModal, setShowIntakeFormLinkModal] = useState(false);

  const setShowInviteClientModalWithPrepopulatedEmail = useCallback((state: boolean) => {
    setShouldPrepopulateEmail(state);
    setShowInviteClientModal(state);
  }, []);

  const closeInviteClientModal = useCallback(() => {
    setShowInviteClientModal(false);
    setShowIntakeFormLinkModal(false);
    setShouldPrepopulateEmail(false);
  }, []);

  return {
    showInviteClientModal,
    setShowInviteClientModal,
    shouldPrepopulateEmail,
    showIntakeFormLinkModal,
    setShowIntakeFormLinkModal,
    setShowInviteClientModalWithPrepopulatedEmail,
    closeInviteClientModal,
  };
};

const useUnsavedSettings = () => {
  const [hasUnsavedSettings, setHasUnsavedSettings] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasUnsavedSettings && pendingNavigationPath) {
      navigate(pendingNavigationPath);
      setPendingNavigationPath(null);
    }
  }, [hasUnsavedSettings, pendingNavigationPath, navigate]);

  return {
    hasUnsavedSettings,
    setHasUnsavedSettings,
    showUnsavedDialog,
    setShowUnsavedDialog,
    pendingNavigationPath,
    setPendingNavigationPath,
  };
};

const useSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return { sidebarOpen, setSidebarOpen };
};

export default function AdvisorDashboardShell() {
  const advisor = useSelector(selectSessionAdvisor);
  const shouldDisallowUsage = useSelector(selectShouldDisallowUsage);
  const { organizationDisplayName } = advisor;
  const dispatch = useDispatch();
  const hasClients = useSelector(selectAdvisorHasClients);
  const priorityStatusByEngagement = useSelector(selectPriorityStatusByEngagement);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const { activePage } = useActivePage();
  const handleClickSignOut = useSignOut();
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const {
    showInviteClientModal,
    setShowInviteClientModal,
    shouldPrepopulateEmail,
    showIntakeFormLinkModal,
    setShowIntakeFormLinkModal,
    setShowInviteClientModalWithPrepopulatedEmail,
    closeInviteClientModal,
  } = useModals();
  const { hasUnsavedSettings, setHasUnsavedSettings, showUnsavedDialog, setShowUnsavedDialog } =
    useUnsavedSettings();

  const {
    navigateToClientsDashboard,
    navigateToWelcomeDashboard,
    navigateToAdvisorProfile,
    navigateToOpportunitiesDashboard,
    navigateToSponsorshipDashboard,
  } = useAdvisorNavigation();

  useEffect(() => {
    dispatch(updatePriorityStatuses(priorityStatusByEngagement));
  }, [dispatch, priorityStatusByEngagement]);

  useFetchQualifyingClients();

  const handleOnPageChange = useCallback(
    (page: DashboardActivePage) => {
      setSidebarOpen(false);
      if (hasUnsavedSettings) {
        setShowUnsavedDialog(true);
        return;
      }

      if (page === 'clients') {
        hasClients ? navigateToClientsDashboard() : navigateToWelcomeDashboard();
      } else if (page === 'settings') {
        navigateToAdvisorProfile();
      } else if (page === 'opportunities') {
        navigateToOpportunitiesDashboard();
      } else if (page === 'sponsorship') {
        navigateToSponsorshipDashboard();
      }
    },
    [
      setSidebarOpen,
      hasUnsavedSettings,
      setShowUnsavedDialog,
      hasClients,
      navigateToClientsDashboard,
      navigateToWelcomeDashboard,
      navigateToAdvisorProfile,
      navigateToOpportunitiesDashboard,
      navigateToSponsorshipDashboard,
    ],
  );

  const renderAdvisorSideBarMobile = useCallback(
    () => (
      <AdvisorDashboardSideBarMobile
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      >
        <AdvisorDashboardSideBarContent
          activePage={activePage}
          onPageChange={handleOnPageChange}
          setShowIntakeFormLinkModal={setShowIntakeFormLinkModal}
          setShowInviteClientModal={setShowInviteClientModal}
          isDisabled={shouldDisallowUsage}
        />
      </AdvisorDashboardSideBarMobile>
    ),
    [
      sidebarOpen,
      setSidebarOpen,
      activePage,
      handleOnPageChange,
      setShowIntakeFormLinkModal,
      setShowInviteClientModal,
      shouldDisallowUsage,
    ],
  );

  const renderSideBarMobileButton = useCallback(
    () => (
      <button
        type="button"
        className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon
          className="h-6 w-6"
          aria-hidden="true"
        />
      </button>
    ),
    [setSidebarOpen],
  );

  return (
    <div className="flex h-screen">
      <AdvisorDashboardSideBar
        advisorSideBarMobile={renderAdvisorSideBarMobile()}
        isSidebarCollapsed={isSidebarCollapsed}
        setIsSidebarCollapsed={setIsSidebarCollapsed}
      >
        <AdvisorDashboardSideBarContent
          key={`mobile-sidebar-${activePage}`}
          activePage={activePage}
          onPageChange={handleOnPageChange}
          setShowIntakeFormLinkModal={setShowIntakeFormLinkModal}
          setShowInviteClientModal={setShowInviteClientModal}
          isDisabled={shouldDisallowUsage}
        />
      </AdvisorDashboardSideBar>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="sticky top-0 z-50">
          {isSidebarCollapsed && (
            <Button
              variant="outline"
              size="icon"
              className="border-primary-300 text-primary-dark hover:text-primary-hover absolute left-4 top-4 z-50 h-9 w-9 lg:flex"
              id="open-sidebar"
              data-testid="open-sidebar"
              onClick={() => setIsSidebarCollapsed(false)}
            >
              <span className="sr-only">Open sidebar</span>
              <Menu className="h-4 w-4" />
            </Button>
          )}
          <AdvisorDashboardTopBar
            advisorSideBarMobileButton={renderSideBarMobileButton()}
            handleClickSignOut={handleClickSignOut}
          />
        </div>
        <main className="flex-1">
          <Routes>
            <Route
              path="/clients"
              element={
                <AdvisorDashboardClients setShowInviteClientModal={setShowInviteClientModal} />
              }
            />

            <Route
              path="/opportunities"
              element={
                <AdvisorDashboardOpportunities
                  setShowInviteClientModal={setShowInviteClientModal}
                  setShowInviteClientModalWithPrepopulatedEmail={
                    setShowInviteClientModalWithPrepopulatedEmail
                  }
                />
              }
            />

            <Route
              path="/settings"
              element={
                <AdvisorDashboardSettings
                  hasUnsavedChanges={hasUnsavedSettings}
                  setHasUnsavedChanges={setHasUnsavedSettings}
                  showUnsavedDialog={showUnsavedDialog}
                  setShowUnsavedDialog={setShowUnsavedDialog}
                />
              }
            />

            <Route
              path="/sponsorship"
              element={<AdvisorAccountSponsorship />}
            />

            <Route
              path="/account-action-required"
              element={<AdvisorAccountActionRequired />}
            />

            <Route
              path="/welcome"
              element={
                <AdvisorWelcome
                  setShowInviteClientModal={setShowInviteClientModal}
                  setShowIntakeFormLinkModal={setShowIntakeFormLinkModal}
                />
              }
            />
          </Routes>
        </main>
      </div>

      <InviteClientModal
        open={showInviteClientModal || showIntakeFormLinkModal}
        onClose={closeInviteClientModal}
        defaultTab={showIntakeFormLinkModal ? 'link' : 'email'}
        shouldPrepopulateEmail={shouldPrepopulateEmail}
      />
    </div>
  );
}
